/**
 * ReplyPreview Component
 * Compact "replying to" pill shown above message composer
 * Shows sender name, content preview (or attachment thumbnail), and cancel button
 */

import type { Message } from '@/types/socketEvents'

interface ReplyPreviewProps {
  replyTo: Message
  onCancel: () => void
}

export function ReplyPreview({ replyTo, onCancel }: ReplyPreviewProps) {
  // Determine what to show: content text or attachment indicator
  const hasAttachments = replyTo.attachments && replyTo.attachments.length > 0
  const hasContent = replyTo.content && replyTo.content.trim().length > 0

  // Get preview text
  const getPreviewText = () => {
    if (hasContent) {
      // Truncate text to ~50 characters
      return replyTo.content.length > 50
        ? replyTo.content.substring(0, 50) + '...'
        : replyTo.content
    }

    if (hasAttachments && replyTo.attachments) {
      const attachment = replyTo.attachments[0]
      const type = attachment.type || 'file'

      // Return emoji + label based on type
      if (type === 'image') return '📷 Photo'
      if (type === 'video') return '🎥 Video'
      if (type === 'audio') return '🎵 Audio'
      return '📎 Attachment'
    }

    return '[Message]'
  }

  const senderName = replyTo.sender?.username || 'Unknown'

  return (
    <div className="flex items-center gap-3 px-4 py-2 bg-blue-50 border-l-4 border-braydin-olive animate-slide-in-up">
      {/* Text content */}
      <div className="flex-1 min-w-0">
        <div className="text-xs font-semibold text-braydin-oliveDark">
          {senderName !== 'Unknown' ? `Replying to ${senderName}` : 'Replying to message'}
        </div>
        <div className="text-sm text-gray-700 truncate">
          {getPreviewText()}
        </div>
      </div>

      {/* Cancel button */}
      <button
        onClick={onCancel}
        className="flex-shrink-0 w-6 h-6 flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded-full transition-colors"
        aria-label="Cancel reply"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  )
}
